import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users,
  Building2,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function AdminPanel() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [reports, setReports] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }

    const fetchAdminData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        };

        const [statsRes, usersRes, verRes, reportsRes, propsRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/stats`, { headers }),
          fetch(`${API_URL}/api/admin/users`, { headers }),
          fetch(`${API_URL}/api/admin/verifications`, { headers }),
          fetch(`${API_URL}/api/admin/reports`, { headers }),
          fetch(`${API_URL}/api/admin/properties`, { headers }),
        ]);

        if (statsRes.ok) setStats(await statsRes.json());
        if (usersRes.ok) setUsers((await usersRes.json()).users || []);
        if (verRes.ok) setVerifications((await verRes.json()).verifications || []);
        if (reportsRes.ok) setReports((await reportsRes.json()).reports || []);
        if (propsRes.ok) setProperties((await propsRes.json()).properties || []);
      } catch (error) {
        // Keep console.error for production error tracking
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [isAdmin, navigate]);

  const handleVerification = async (verificationId, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/verifications/${verificationId}?status=${status}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success(`Verification ${status}`);
        setVerifications(prev =>
          prev.map(v => v.verification_id === verificationId ? { ...v, status } : v)
        );
      }
    } catch (error) {
      toast.error('Failed to update verification');
    }
  };

  const handleReport = async (reportId, status, action) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/admin/reports/${reportId}?status=${status}${action ? `&action=${action}` : ''}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Report updated');
        setReports(prev =>
          prev.map(r => r.report_id === reportId ? { ...r, status } : r)
        );
      }
    } catch (error) {
      toast.error('Failed to update report');
    }
  };

  const handleModeration = async (propertyId, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/admin/properties/${propertyId}/moderation?status=${status}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        toast.success(`Property ${status}`);
        setProperties(prev =>
          prev.map(p => p.property_id === propertyId ? { ...p, moderation_status: status } : p)
        );
      }
    } catch (error) {
      toast.error('Failed to update property');
    }
  };

  if (!isAdmin) {
    return null;
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Total Properties', value: stats?.total_properties || 0, icon: Building2, color: 'text-purple-500', bg: 'bg-purple-50' },
    { label: 'Active Listings', value: stats?.active_properties || 0, icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    { label: 'Pending Verifications', value: stats?.pending_verifications || 0, icon: Shield, color: 'text-amber-500', bg: 'bg-amber-50' },
    { label: 'Pending Reports', value: stats?.pending_reports || 0, icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50' },
  ];

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      flagged: 'bg-orange-100 text-orange-700',
      resolved: 'bg-slate-100 text-slate-700',
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-panel">
      <Navbar />

      <div className="pt-20 pb-12">
        <div className="rh-container">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-2">
              Admin Panel
            </h1>
            <p className="text-slate-500">
              Manage users, listings, and platform operations
            </p>
          </div>

          {/* Stats */}
          {loading ? (
            <div className="grid md:grid-cols-5 gap-4 mb-8">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-24 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-5 gap-4 mb-8">
              {statCards.map((stat) => (
                <Card key={stat.label} className="border-0 shadow-soft">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center`}>
                        <stat.icon className={`w-5 h-5 ${stat.color}`} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                        <p className="text-xs text-slate-500">{stat.label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Main Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white p-1 rounded-full shadow-sm mb-6">
              <TabsTrigger value="overview" className="rounded-full">Overview</TabsTrigger>
              <TabsTrigger value="verifications" className="rounded-full">
                Verifications
                {stats?.pending_verifications > 0 && (
                  <Badge className="ml-2 bg-amber-500">{stats.pending_verifications}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="reports" className="rounded-full">
                Reports
                {stats?.pending_reports > 0 && (
                  <Badge className="ml-2 bg-red-500">{stats.pending_reports}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="properties" className="rounded-full">Properties</TabsTrigger>
              <TabsTrigger value="users" className="rounded-full">Users</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-0 shadow-soft">
                  <CardHeader>
                    <CardTitle className="font-serif">Recent Verifications</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {verifications.slice(0, 5).map((ver) => (
                      <div key={ver.verification_id} className="flex items-center justify-between py-3 border-b last:border-0">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback>{ver.user?.name?.[0] || 'U'}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{ver.user?.name || 'Unknown'}</span>
                        </div>
                        <Badge className={statusBadge(ver.status)}>{ver.status}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-soft">
                  <CardHeader>
                    <CardTitle className="font-serif">Recent Reports</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reports.slice(0, 5).map((rep) => (
                      <div key={rep.report_id} className="flex items-center justify-between py-3 border-b last:border-0">
                        <div>
                          <p className="text-sm font-medium">{rep.reason}</p>
                          <p className="text-xs text-slate-500">{rep.property?.title?.slice(0, 30)}...</p>
                        </div>
                        <Badge className={statusBadge(rep.status)}>{rep.status}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Verifications Tab */}
            <TabsContent value="verifications">
              <Card className="border-0 shadow-soft">
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">User</th>
                        <th className="text-left p-4 font-medium text-slate-600">Document</th>
                        <th className="text-left p-4 font-medium text-slate-600">Status</th>
                        <th className="text-left p-4 font-medium text-slate-600">Date</th>
                        <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {verifications.map((ver) => (
                        <tr key={ver.verification_id} className="border-t">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-8 h-8">
                                <AvatarImage src={ver.user?.picture} />
                                <AvatarFallback>{ver.user?.name?.[0]}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-slate-900">{ver.user?.name}</p>
                                <p className="text-xs text-slate-500">{ver.user?.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-slate-600">{ver.document_type}</td>
                          <td className="p-4">
                            <Badge className={statusBadge(ver.status)}>{ver.status}</Badge>
                          </td>
                          <td className="p-4 text-slate-500 text-sm">
                            {new Date(ver.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4 text-right">
                            {ver.status === 'pending' && (
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  className="bg-emerald-500 hover:bg-emerald-600 rounded-full"
                                  onClick={() => handleVerification(ver.verification_id, 'approved')}
                                  data-testid={`approve-ver-${ver.verification_id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="rounded-full"
                                  onClick={() => handleVerification(ver.verification_id, 'rejected')}
                                  data-testid={`reject-ver-${ver.verification_id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports">
              <Card className="border-0 shadow-soft">
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">Property</th>
                        <th className="text-left p-4 font-medium text-slate-600">Reason</th>
                        <th className="text-left p-4 font-medium text-slate-600">Reporter</th>
                        <th className="text-left p-4 font-medium text-slate-600">Status</th>
                        <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((rep) => (
                        <tr key={rep.report_id} className="border-t">
                          <td className="p-4">
                            <p className="font-medium text-slate-900 line-clamp-1">{rep.property?.title}</p>
                            <p className="text-xs text-slate-500">{rep.property?.location?.city}</p>
                          </td>
                          <td className="p-4 text-slate-600">{rep.reason}</td>
                          <td className="p-4 text-slate-600">{rep.reporter?.name}</td>
                          <td className="p-4">
                            <Badge className={statusBadge(rep.status)}>{rep.status}</Badge>
                          </td>
                          <td className="p-4 text-right">
                            {rep.status === 'pending' && (
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full"
                                  onClick={() => handleReport(rep.report_id, 'resolved')}
                                >
                                  Dismiss
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="rounded-full"
                                  onClick={() => handleReport(rep.report_id, 'resolved', 'suspend_property')}
                                  data-testid={`suspend-prop-${rep.report_id}`}
                                >
                                  Suspend Property
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Properties Tab */}
            <TabsContent value="properties">
              <Card className="border-0 shadow-soft">
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">Property</th>
                        <th className="text-left p-4 font-medium text-slate-600">Owner</th>
                        <th className="text-left p-4 font-medium text-slate-600">Status</th>
                        <th className="text-left p-4 font-medium text-slate-600">Moderation</th>
                        <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {properties.map((prop) => (
                        <tr key={prop.property_id} className="border-t">
                          <td className="p-4">
                            <p className="font-medium text-slate-900 line-clamp-1">{prop.title}</p>
                            <p className="text-xs text-slate-500">${prop.monthly_rent}/mo</p>
                          </td>
                          <td className="p-4 text-slate-600">{prop.owner?.name}</td>
                          <td className="p-4">
                            <Badge className={statusBadge(prop.status)}>{prop.status}</Badge>
                          </td>
                          <td className="p-4">
                            <Badge className={statusBadge(prop.moderation_status)}>{prop.moderation_status}</Badge>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full"
                                onClick={() => navigate(`/property/${prop.property_id}`)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {prop.moderation_status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-emerald-500 hover:bg-emerald-600 rounded-full"
                                    onClick={() => handleModeration(prop.property_id, 'approved')}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="rounded-full"
                                    onClick={() => handleModeration(prop.property_id, 'rejected')}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card className="border-0 shadow-soft">
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">User</th>
                        <th className="text-left p-4 font-medium text-slate-600">Role</th>
                        <th className="text-left p-4 font-medium text-slate-600">Verified</th>
                        <th className="text-left p-4 font-medium text-slate-600">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.user_id} className="border-t">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-8 h-8">
                                <AvatarImage src={user.picture} />
                                <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-slate-900">{user.name}</p>
                                <p className="text-xs text-slate-500">{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge variant="outline">{user.role}</Badge>
                          </td>
                          <td className="p-4">
                            {user.is_verified_owner ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-slate-300" />
                            )}
                          </td>
                          <td className="p-4 text-slate-500 text-sm">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
